import React, { useEffect, useLayoutEffect, useState, useContext, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import classNames from "classnames";
import { AuthContext } from "../../../../context/authcontext";
import {
    CalendarService,
    CalendarTask,
    CalendarTaskGroup,
} from "../calendar/calendar.service";
import "../calendar/calendar.css";
import "./externalTask.css";
import axios from "axios";
import { API_BASE_URL } from "../../../../environment";
import { getAuthHeaders } from "../../../../utils/authUtils";
import TaskDetailModal from "../../../../components/TaskDetailModal";

interface NewTaskForm {
    title: string;
    description: string;
    dueDate: string;
    dueTime: string;
    hasDueDate: boolean;
    listId: number | null;
    subtasks: string[];
}

const INITIAL_FORM: NewTaskForm = {
    title: "",
    description: "",
    dueDate: dayjs().format("YYYY-MM-DD"),
    dueTime: dayjs().format("HH:mm"),
    hasDueDate: false,
    listId: null,
    subtasks: [],
};

interface EditTaskForm {
    title: string;
    notes: string;
    dueDate: string;
    dueTime: string;
    hasDueDate: boolean;
}

interface CRMTask {
    task_id: number;
    title: string;
    category: string;
    start_date: string | null;
    due_date: string | null;
    priority: string;
    status: string;
    description: string;
    related_entity_name: string | null;
    related_entity_id: number | null;
    is_deleted: boolean;
    created_by: {
        user_id: number;
        full_name: string;
        avatar_url: string;
    } | null;
    tenant_id: number;
    related_entity_title: string | null;
}

type SortMode = "my_order" | "date" | "title";

const SERVER_TASK_FIELDS: (keyof CalendarTask)[] = [
    "title",
    "notes",
    "due_datetime",
    "status",
    "is_completed",
    "completed_datetime",
    "updated_datetime",
];

const extractServerTask = (result: any): CalendarTask | null =>
    [result?.data?.data, result?.data, result].find(
        (candidate) => candidate && typeof candidate === "object" && candidate.calendar_task_id
    ) ?? null;

const pickServerFields = (task: CalendarTask | null): Partial<CalendarTask> => {
    if (!task) return {};
    const picked: Partial<CalendarTask> = {};
    SERVER_TASK_FIELDS.forEach((field) => {
        if (task[field] !== undefined) (picked as any)[field] = task[field];
    });
    return picked;
};

interface TaskError {
    title: string;
    message: string;
    code?: number;
    detail?: string;
}

const KNOWN_TASK_ERRORS: { pattern: RegExp; message: string }[] = [
    {
        pattern: /recurrent tasks cannot have subtasks/i,
        message: "Google Tasks doesn't allow subtasks on recurring tasks. Add the subtask to a non-recurring task instead.",
    },
];

const parseTaskError = (err: any, title: string, fallback: string): TaskError => {
    const data = err?.response?.data;
    const raw = [data?.message, data?.error?.message, data?.detail, data?.error, err?.message].find(
        (value) => typeof value === "string" && value.trim()
    ) as string | undefined;
    let code: number | undefined = typeof err?.response?.status === "number" ? err.response.status : undefined;
    let detail: string | undefined;
    let context = raw?.trim() ?? "";
    const jsonStart = context.indexOf("{");
    if (jsonStart >= 0) {
        try {
            const nested = JSON.parse(context.slice(jsonStart));
            const nestedMessage = nested?.error?.message ?? nested?.message;
            if (typeof nestedMessage === "string") detail = nestedMessage.trim();
            if (typeof nested?.error?.code === "number") code = nested.error.code;
            context = context.slice(0, jsonStart).replace(/[s:]+$/, "").trim();
        } catch { }
    }
    const reason = detail || context;
    const known = KNOWN_TASK_ERRORS.find((entry) => entry.pattern.test(reason));
    if (known) return { title, message: known.message, code, detail: reason };
    if (detail) return { title, message: detail, code, detail: context || undefined };
    return { title, message: context || fallback, code };
};

const isChildOf = (task: CalendarTask, parent: CalendarTask) => {
    if (!task.parent_task_id) return false;
    const parentRef = String(task.parent_task_id);
    return parentRef === String(parent.calendar_task_id) || (!!parent.external_task_id && parentRef === parent.external_task_id);
};
type SidebarFilter = "all" | "starred";

const ExternalTask: React.FC = () => {
    const navigate = useNavigate();
    const { selectedTenantId, auth } = useContext(AuthContext);
    const userId = auth?.user_id;

    const [tasks, setTasks] = useState<CalendarTask[]>([]);
    const [allTaskGroups, setAllTaskGroups] = useState<CalendarTaskGroup[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number | string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [sidebarFilter, setSidebarFilter] = useState<SidebarFilter>("all");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const [starredTaskIds, setStarredTaskIds] = useState<Set<number>>(() => {
        const stored = localStorage.getItem("starredExternalTasks");
        return stored ? new Set(JSON.parse(stored)) : new Set();
    });

    const [sortModes, setSortModes] = useState<Record<number | string, SortMode>>({});
    const [sortMenuOpen, setSortMenuOpen] = useState<number | string | null>(null);
    const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(new Set());
    const [taskMenuOpen, setTaskMenuOpen] = useState<number | null>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; anchorTop: number }>({ top: 0, left: 0, anchorTop: 0 });
    const [completingTaskIds, setCompletingTaskIds] = useState<Set<number>>(new Set());

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState<NewTaskForm>({ ...INITIAL_FORM });
    const [activeGroupForCreate, setActiveGroupForCreate] = useState<number | null>(null);
    const [newSubtaskInput, setNewSubtaskInput] = useState("");
    const [savingTask, setSavingTask] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTask, setEditingTask] = useState<CalendarTask | null>(null);
    const [editForm, setEditForm] = useState<EditTaskForm>({ title: "", notes: "", dueDate: "", dueTime: "", hasDueDate: false });
    const [editNewSubtaskInput, setEditNewSubtaskInput] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);

    const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<number | null>(null);
    const [completedVisible, setCompletedVisible] = useState<Record<number | string, boolean>>({});

    const [inlineSubtaskParentId, setInlineSubtaskParentId] = useState<number | null>(null);
    const [inlineSubtaskTitle, setInlineSubtaskTitle] = useState("");
    const [inlineSubtaskNotes, setInlineSubtaskNotes] = useState("");
    const [inlineSubtaskDueDate, setInlineSubtaskDueDate] = useState("");

    const [taskError, setTaskError] = useState<TaskError | null>(null);
    const showTaskError = (err: unknown, title: string, fallback: string) => {
        console.error(err);
        setTaskError(parseTaskError(err, title, fallback));
    };

    const [deadlineTaskId, setDeadlineTaskId] = useState<number | null>(null);
    const [deadlineDate, setDeadlineDate] = useState("");

    const sortMenuRef = useRef<HTMLDivElement>(null);
    const taskMenuRef = useRef<HTMLDivElement>(null);
    const taskDropdownRef = useRef<HTMLDivElement>(null);
    const inlineSubtaskDateRef = useRef<HTMLInputElement>(null);
    const deadlineDateInputRef = useRef<HTMLInputElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
    const [taskDetails, setTaskDetails] = useState<any>(null);
    const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);
    const [isEditingTask, setIsEditingTask] = useState(false);
    const [calendarAuthsLoaded, setCalendarAuthsLoaded] = useState(false);
    const [crmTasks, setCrmTasks] = useState<CRMTask[]>([]);
    const [crmTasksLoading, setCrmTasksLoading] = useState(false);
    const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalTasksCount, setTotalTasksCount] = useState(0);
    const [totalCrmTasksCount, setTotalCrmTasksCount] = useState(0);
    const [crmNextPageUrl, setCrmNextPageUrl] = useState<string | null>(null);
    const [crmTasksLoadingMore, setCrmTasksLoadingMore] = useState(false);

    const handleOpenCRMTask = async (taskId: number) => {
        setIsTaskModalVisible(true);
        setIsLoadingTaskDetails(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/task/${taskId}/`, {
                headers: getAuthHeaders(),
            });
            setTaskDetails(response.data);
        } catch (err) {
            console.error("Error loading task details:", err);
        } finally {
            setIsLoadingTaskDetails(false);
        }
    };

    const tasksByGroup = useMemo(() => {
        const map: Record<number | string, CalendarTask[]> = {};
        tasks.forEach((t) => {
            const gid = t.calendar_task_group?.calendar_task_group_id || t.calendar_task_group_id;
            const key = gid || "unsynced";
            if (!map[key]) map[key] = [];
            map[key].push(t);
        });
        return map;
    }, [tasks]);

    useEffect(() => {
        localStorage.setItem("starredExternalTasks", JSON.stringify(Array.from(starredTaskIds)));
    }, [starredTaskIds]);

    const fetchTasks = useCallback(async (silent: boolean = false) => {
        if (!selectedTenantId) return;
        if (!silent) setLoading(true);
        try {
            const { tasks: fetchedTasks, next, count } = await CalendarService.fetchTasksPaginated(selectedTenantId);
            setTasks(fetchedTasks);
            setNextPageUrl(next);
            setTotalTasksCount(count || 0);
        } catch { }
        finally {
            if (!silent) setLoading(false);
        }
    }, [selectedTenantId]);

    const refreshFirstPage = useCallback(async () => {
        if (!selectedTenantId) return;
        try {
            const { tasks: fresh, count } = await CalendarService.fetchTasksPaginated(selectedTenantId);
            setTasks((prev) => {
                const freshById = new Map(fresh.map((t) => [t.calendar_task_id, t]));
                const prevIds = new Set(prev.map((t) => t.calendar_task_id));
                const added = fresh.filter((t) => !prevIds.has(t.calendar_task_id));
                return [...added, ...prev.map((t) => freshById.get(t.calendar_task_id) ?? t)];
            });
            setTotalTasksCount(count || 0);
        } catch { }
    }, [selectedTenantId]);

    const patchTask = useCallback((taskId: number, patch: Partial<CalendarTask>) => {
        setTasks((prev) =>
            prev.map((t) => {
                const self = t.calendar_task_id === taskId ? { ...t, ...patch } : t;
                if (!self.sub_tasks?.some((sub) => sub.calendar_task_id === taskId)) return self;
                return {
                    ...self,
                    sub_tasks: self.sub_tasks.map((sub) => (sub.calendar_task_id === taskId ? { ...sub, ...patch } : sub)),
                };
            })
        );
    }, []);

    const removeTask = useCallback((taskId: number) => {
        setTasks((prev) => {
            const target = prev.find((t) => t.calendar_task_id === taskId);
            return prev
                .filter((t) => t.calendar_task_id !== taskId && !(target && isChildOf(t, target)))
                .map((t) =>
                    t.sub_tasks?.some((sub) => sub.calendar_task_id === taskId)
                        ? { ...t, sub_tasks: t.sub_tasks.filter((sub) => sub.calendar_task_id !== taskId) }
                        : t
                );
        });
    }, []);

    const insertTask = useCallback((task: CalendarTask, parent?: CalendarTask) => {
        const group = parent?.calendar_task_group;
        const created: CalendarTask = {
            ...task,
            sub_tasks: task.sub_tasks ?? [],
            calendar_task_group_id: task.calendar_task_group_id ?? parent?.calendar_task_group_id,
            calendar_task_group: task.calendar_task_group ?? group,
            parent_task_id: parent ? task.parent_task_id ?? String(parent.calendar_task_id) : task.parent_task_id ?? null,
        };
        setTasks((prev) => {
            if (prev.some((t) => t.calendar_task_id === created.calendar_task_id)) return prev;
            if (!parent) return [created, ...prev];
            return [
                ...prev.map((t) =>
                    t.calendar_task_id === parent.calendar_task_id
                        ? { ...t, sub_tasks: [...(t.sub_tasks ?? []), created] }
                        : t
                ),
                created,
            ];
        });
    }, []);

    const loadMoreTasks = useCallback(async () => {
        if (!nextPageUrl || loadingMore || !selectedTenantId) return;
        setLoadingMore(true);
        try {
            const { tasks: moreTasks, next } = await CalendarService.fetchTasksPaginated(selectedTenantId, nextPageUrl);
            setTasks(prev => {
                const prevIds = new Set(prev.map(t => t.calendar_task_id));
                const uniqueMoreTasks = moreTasks.filter(t => !prevIds.has(t.calendar_task_id));
                return [...prev, ...uniqueMoreTasks];
            });
            setNextPageUrl(next);
        } catch { }
        finally {
            setLoadingMore(false);
        }
    }, [nextPageUrl, loadingMore, selectedTenantId]);

    const fetchTaskGroups = useCallback(async () => {
        if (!selectedTenantId || !userId) return;
        setGroupsLoading(true);
        try {
            const auths = await CalendarService.fetchCalendarAuths(selectedTenantId, userId);
            const allGroupsPromises = auths.map(a =>
                CalendarService.fetchTaskGroups(selectedTenantId, a.calendar_auth_id)
            );
            const groupsResults = await Promise.all(allGroupsPromises);
            const flatGroups = groupsResults.flat();

            const seen = new Set();
            const uniqueGroups = flatGroups.filter(g => {
                if (seen.has(g.calendar_task_group_id)) return false;
                seen.add(g.calendar_task_group_id);
                return true;
            });

            setAllTaskGroups(uniqueGroups);
            setSelectedGroupIds((prev) => {
                if (prev.size > 0) {
                    const next = new Set(prev);
                    return next;
                }
                const ids = uniqueGroups.map(g => g.calendar_task_group_id);
                return new Set(ids);
            });
        } catch (err) {
            console.error(err);
        } finally {
            setGroupsLoading(false);
            setCalendarAuthsLoaded(true);
        }
    }, [selectedTenantId, userId]);

    useEffect(() => {
        fetchTasks();
        fetchTaskGroups();
    }, [fetchTasks, fetchTaskGroups]);

    const fetchCrmTasks = useCallback(() => {
        if (!selectedTenantId || !calendarAuthsLoaded || allTaskGroups.length > 0) return;
        setCrmTasksLoading(true);
        axios.get(`${API_BASE_URL}/task/?tenant_id=${selectedTenantId}&limit=50`, {
            headers: getAuthHeaders(),
        }).then((res) => {
            setCrmTasks(res.data.results || []);
            setCrmNextPageUrl(res.data.next || null);
            setTotalCrmTasksCount(res.data.count || 0);
        }).catch(() => { }).finally(() => setCrmTasksLoading(false));
    }, [selectedTenantId, calendarAuthsLoaded, allTaskGroups.length]);

    const loadMoreCrmTasks = useCallback(() => {
        if (!crmNextPageUrl || crmTasksLoadingMore || !selectedTenantId) return;
        setCrmTasksLoadingMore(true);
        axios.get(crmNextPageUrl, {
            headers: getAuthHeaders(),
        }).then((res) => {
            setCrmTasks(prev => {
                const prevIds = new Set(prev.map(t => t.task_id));
                const uniqueNew = (res.data.results || []).filter((t: any) => !prevIds.has(t.task_id));
                return [...prev, ...uniqueNew];
            });
            setCrmNextPageUrl(res.data.next || null);
        }).catch(() => { }).finally(() => setCrmTasksLoadingMore(false));
    }, [crmNextPageUrl, crmTasksLoadingMore, selectedTenantId]);

    useEffect(() => {
        fetchCrmTasks();
    }, [fetchCrmTasks]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) {
                setSortMenuOpen(null);
            }
            if (taskMenuRef.current && !taskMenuRef.current.contains(e.target as Node)) {
                setTaskMenuOpen(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useLayoutEffect(() => {
        const el = taskDropdownRef.current;
        if (taskMenuOpen === null || !el) return;
        const { height } = el.getBoundingClientRect();
        if (menuPos.top + height <= window.innerHeight - 8) return;
        const flipped = Math.max(8, menuPos.anchorTop - height - 4);
        if (flipped !== menuPos.top) setMenuPos((prev) => ({ ...prev, top: flipped }));
    }, [taskMenuOpen, menuPos]);

    const toggleTaskMenu = (e: React.MouseEvent<HTMLElement>, taskId: number) => {
        e.stopPropagation();
        if (taskMenuOpen === taskId) {
            setTaskMenuOpen(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.right - 180, anchorTop: rect.top });
        setTaskMenuOpen(taskId);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    if (calendarAuthsLoaded && allTaskGroups.length === 0) {
                        loadMoreCrmTasks();
                    } else {
                        loadMoreTasks();
                    }
                }
            },
            { threshold: 0.1 }
        );
        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }
        return () => observer.disconnect();
    }, [loadMoreTasks, loadMoreCrmTasks, calendarAuthsLoaded, allTaskGroups.length]);

    const applySorting = useCallback((taskList: CalendarTask[], groupId: number | string): CalendarTask[] => {
        const mode = sortModes[groupId as any] || "my_order";
        if (mode === "my_order") return taskList;
        const sorted = [...taskList];
        if (mode === "date") {
            sorted.sort((a, b) => {
                const da = a.due_datetime ? new Date(a.due_datetime).getTime() : Infinity;
                const db = b.due_datetime ? new Date(b.due_datetime).getTime() : Infinity;
                return da - db;
            });
        } else if (mode === "title") {
            sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        }
        return sorted;
    }, [sortModes]);

    const derivedGroups = useMemo(() => {
        const groupMap = new Map<number, CalendarTaskGroup>();
        tasks.forEach((t) => {
            if (t.calendar_task_group && !groupMap.has(t.calendar_task_group.calendar_task_group_id)) {
                groupMap.set(t.calendar_task_group.calendar_task_group_id, t.calendar_task_group);
            }
        });
        return Array.from(groupMap.values());
    }, [tasks]);

    const filteredGroups = useMemo(() => {
        const baseGroups = [...(allTaskGroups.length > 0 ? allTaskGroups : derivedGroups)];

        if (tasksByGroup["unsynced"]?.length) {
            baseGroups.push({
                calendar_task_group_id: "unsynced" as any,
                task_group_name: "Not Synced",
            } as CalendarTaskGroup);
        }

        const displayedGroups = baseGroups.filter(g =>
            selectedGroupIds.size === 0 && allTaskGroups.length === 0
                ? true
                : selectedGroupIds.has(g.calendar_task_group_id)
        );
        if (sidebarFilter === "starred") {
            return displayedGroups.filter((g) => {
                const groupTasks = tasksByGroup[g.calendar_task_group_id] || [];
                return groupTasks.some((t) => starredTaskIds.has(t.calendar_task_id));
            });
        }
        return displayedGroups;
    }, [allTaskGroups, derivedGroups, selectedGroupIds, tasksByGroup, sidebarFilter, starredTaskIds]);

    const getFilteredTasksForGroup = (groupId: number | string) => {
        const groupTasks = tasksByGroup[groupId] || [];
        if (sidebarFilter === "starred") {
            return groupTasks.filter((t) => starredTaskIds.has(t.calendar_task_id));
        }
        return groupTasks;
    };

    const toggleGroupSelection = (groupId: number | string) => {
        setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const toggleTaskCompletion = async (taskId: number) => {
        const task = tasks.find((t) => t.calendar_task_id === taskId);
        if (!task) return;

        setCompletingTaskIds((prev) => new Set(prev).add(taskId));

        const newCompleted = !task.is_completed;
        const payload: any = {
            calendar_auth_id: task.calendar_auth_id,
            task_group_id: task.calendar_task_group_id,
            calendar_task_id: task.calendar_task_id,
            is_completed: newCompleted,
        };

        if (newCompleted) {
            payload.completed_datetime = dayjs().toISOString();
        }

        try {
            const result = await CalendarService.updateTask(payload);
            patchTask(taskId, {
                is_completed: newCompleted,
                completed_datetime: newCompleted ? payload.completed_datetime : null,
                ...pickServerFields(extractServerTask(result)),
            });
        } catch (err) {
            showTaskError(err, "Couldn't update task", "The task status could not be changed. Please try again.");
        } finally {
            setCompletingTaskIds((prev) => {
                const next = new Set(prev);
                next.delete(taskId);
                return next;
            });
        }
    };

    const handleSaveTask = async () => {
        if (!createForm.title || !createForm.listId) return;

        const group = allTaskGroups.find((g) => g.calendar_task_group_id === createForm.listId)
            || derivedGroups.find((g) => g.calendar_task_group_id === createForm.listId);

        if (!group) return;

        setSavingTask(true);

        const payload: any = {
            calendar_auth_id: group.calendar_auth_id,
            task_group_id: group.calendar_task_group_id,
            title: createForm.title,
            notes: createForm.description,
            is_completed: false,
        };

        if (createForm.hasDueDate) {
            payload.due_datetime = dayjs(`${createForm.dueDate}T${createForm.dueTime}`).toISOString();
        }

        try {
            const result = await CalendarService.createTask(payload);
            const createdTask = extractServerTask(result);
            let complete = !!createdTask;
            if (createdTask) {
                insertTask({ ...createdTask, calendar_task_group: createdTask.calendar_task_group ?? group });
            }

            const parentTaskId = createdTask?.calendar_task_id;
            if (createForm.subtasks.length > 0 && parentTaskId) {
                for (const subtaskTitle of createForm.subtasks) {
                    if (!subtaskTitle.trim()) continue;
                    const subResult = await CalendarService.createTask({
                        calendar_auth_id: group.calendar_auth_id,
                        task_group_id: group.calendar_task_group_id,
                        title: subtaskTitle,
                        notes: "",
                        is_completed: false,
                        parent_task_id: parentTaskId,
                    });
                    const createdSub = extractServerTask(subResult);
                    if (createdSub && createdTask) insertTask(createdSub, { ...createdTask, calendar_task_group: group });
                    else complete = false;
                }
            }

            if (!complete) refreshFirstPage();
            closeCreateModal();
        } catch (err) {
            showTaskError(err, "Couldn't create task", "The task could not be created. Please try again.");
        } finally {
            setSavingTask(false);
        }
    };

    const handleDeleteTask = async (taskId: number) => {
        const task = tasks.find((t) => t.calendar_task_id === taskId);
        if (!task) return;

        try {
            await CalendarService.deleteTask({
                calendar_auth_id: task.calendar_auth_id,
                task_group_id: task.calendar_task_group_id,
                calendar_task_id: task.calendar_task_id,
            });
            removeTask(taskId);
            if (!task.parent_task_id) setTotalTasksCount((count) => Math.max(0, count - 1));
            setTaskMenuOpen(null);
            setDeleteConfirmTaskId(null);
        } catch (err) {
            showTaskError(err, "Couldn't delete task", "The task could not be deleted. Please try again.");
        }
    };

    const openEditModal = (task: CalendarTask) => {
        setEditingTask(task);
        const hasDue = !!task.due_datetime;
        setEditForm({
            title: task.title,
            notes: task.notes || "",
            dueDate: hasDue ? dayjs(task.due_datetime).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
            dueTime: hasDue ? dayjs(task.due_datetime).format("HH:mm") : dayjs().format("HH:mm"),
            hasDueDate: hasDue,
        });
        setEditNewSubtaskInput("");
        setShowEditModal(true);
        setTaskMenuOpen(null);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingTask(null);
        setEditNewSubtaskInput("");
    };

    const handleUpdateTask = async () => {
        if (!editingTask) return;

        setSavingEdit(true);

        const payload: any = {
            calendar_auth_id: editingTask.calendar_auth_id,
            task_group_id: editingTask.calendar_task_group_id,
            calendar_task_id: editingTask.calendar_task_id,
        };

        if (editForm.title !== editingTask.title) {
            payload.title = editForm.title;
        }
        if (editForm.notes !== (editingTask.notes || "")) {
            payload.notes = editForm.notes;
        }

        if (editForm.hasDueDate) {
            const newDue = dayjs(`${editForm.dueDate}T${editForm.dueTime}`).toISOString();
            if (newDue !== editingTask.due_datetime) {
                payload.due_datetime = newDue;
            }
        } else if (editingTask.due_datetime) {
            payload.due_datetime = null;
        }

        try {
            if (Object.keys(payload).length > 3) {
                const result = await CalendarService.updateTask(payload);
                const changes = { ...payload };
                delete changes.calendar_auth_id;
                delete changes.task_group_id;
                delete changes.calendar_task_id;
                patchTask(editingTask.calendar_task_id, { ...changes, ...pickServerFields(extractServerTask(result)) });
            }
            closeEditModal();
        } catch (err) {
            showTaskError(err, "Couldn't save changes", "Your changes could not be saved. Please try again.");
        } finally {
            setSavingEdit(false);
        }
    };

    const handleAddSubtaskInEdit = async () => {
        if (!editingTask || !editNewSubtaskInput.trim()) return;

        try {
            const result = await CalendarService.createTask({
                calendar_auth_id: editingTask.calendar_auth_id,
                task_group_id: editingTask.calendar_task_group_id,
                title: editNewSubtaskInput.trim(),
                notes: "",
                is_completed: false,
                parent_task_id: editingTask.calendar_task_id,
            });
            setEditNewSubtaskInput("");
            const createdSub = extractServerTask(result);
            if (createdSub) insertTask(createdSub, editingTask);
            else refreshFirstPage();
        } catch (err) {
            showTaskError(err, "Couldn't add subtask", "The subtask could not be created. Please try again.");
        }
    };

    const handleInlineSubtaskSubmit = async () => {
        if (!inlineSubtaskParentId || !inlineSubtaskTitle.trim()) return;

        const parentTask = tasks.find((t) => t.calendar_task_id === inlineSubtaskParentId);
        if (!parentTask) return;

        const payload: any = {
            calendar_auth_id: parentTask.calendar_auth_id,
            task_group_id: parentTask.calendar_task_group_id,
            title: inlineSubtaskTitle.trim(),
            notes: inlineSubtaskNotes,
            is_completed: false,
            parent_task_id: parentTask.calendar_task_id,
        };

        if (inlineSubtaskDueDate) {
            payload.due_datetime = dayjs(inlineSubtaskDueDate).startOf("day").toISOString();
        }

        try {
            const result = await CalendarService.createTask(payload);
            setInlineSubtaskTitle("");
            setInlineSubtaskNotes("");
            setInlineSubtaskDueDate("");
            const createdSub = extractServerTask(result);
            if (createdSub) {
                insertTask(createdSub, parentTask);
                setExpandedTaskIds((prev) => new Set(prev).add(parentTask.calendar_task_id));
            } else {
                refreshFirstPage();
            }
        } catch (err) {
            showTaskError(err, "Couldn't add subtask", "The subtask could not be created. Please try again.");
        }
    };

    const closeInlineSubtask = () => {
        setInlineSubtaskParentId(null);
        setInlineSubtaskTitle("");
        setInlineSubtaskNotes("");
        setInlineSubtaskDueDate("");
    };

    const handleAddDeadline = async (taskId: number, dateStr: string) => {
        const task = tasks.find((t) => t.calendar_task_id === taskId);
        if (!task) return;

        try {
            const dueDatetime = dayjs(dateStr).startOf("day").toISOString();
            const result = await CalendarService.updateTask({
                calendar_auth_id: task.calendar_auth_id,
                task_group_id: task.calendar_task_group_id,
                calendar_task_id: task.calendar_task_id,
                due_datetime: dueDatetime,
            });
            setDeadlineTaskId(null);
            setDeadlineDate("");
            patchTask(taskId, { due_datetime: dueDatetime, ...pickServerFields(extractServerTask(result)) });
        } catch (err) {
            showTaskError(err, "Couldn't add deadline", "The deadline could not be saved. Please try again.");
        }
    };

    const toggleStar = (taskId: number) => {
        setStarredTaskIds((prev) => {
            const next = new Set(prev);
            if (next.has(taskId)) {
                next.delete(taskId);
            } else {
                next.add(taskId);
            }
            return next;
        });
    };

    const openCreateModal = (groupId: number) => {
        setActiveGroupForCreate(groupId);
        setCreateForm({ ...INITIAL_FORM, listId: groupId });
        setNewSubtaskInput("");
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setCreateForm({ ...INITIAL_FORM });
        setActiveGroupForCreate(null);
        setNewSubtaskInput("");
    };

    const addSubtaskToCreate = () => {
        if (!newSubtaskInput.trim()) return;
        setCreateForm((prev) => ({ ...prev, subtasks: [...prev.subtasks, newSubtaskInput.trim()] }));
        setNewSubtaskInput("");
    };

    const removeSubtaskFromCreate = (index: number) => {
        setCreateForm((prev) => ({
            ...prev,
            subtasks: prev.subtasks.filter((_, i) => i !== index),
        }));
    };

    const getPriorityBadge = (priority: string) => {
        const p = priority?.toLowerCase();
        if (p === "high") return { color: "#ef4444", bg: "rgba(239,68,68,0.1)", label: "High" };
        if (p === "medium") return { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", label: "Medium" };
        if (p === "low") return { color: "#22c55e", bg: "rgba(34,197,94,0.1)", label: "Low" };
        return null;
    };

    const formatDueDate = (dateStr: string | null) => {
        if (!dateStr) return null;
        const due = dayjs(dateStr);
        if (!due.isValid()) return null;
        const today = dayjs();
        if (due.isSame(today, "day")) return "Today";
        if (due.isSame(today.add(1, "day"), "day")) return "Tomorrow";
        return due.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    const isDuePast = (dateStr: string | null) => {
        if (!dateStr) return false;
        return dayjs(dateStr).isBefore(dayjs(), "day");
    };

    const getSubtasksForTask = (task: CalendarTask) => {
        return task.sub_tasks || [];
    };

    const toggleTaskExpanded = (taskId: number) => {
        setExpandedTaskIds((prev) => {
            const next = new Set(prev);
            if (next.has(taskId)) {
                next.delete(taskId);
            } else {
                next.add(taskId);
            }
            return next;
        });
    };

    const renderInlineSubtaskForm = (parentTaskId: number) => {
        if (inlineSubtaskParentId !== parentTaskId) return null;

        return (
            <div className="ext-task-inline-subtask-form">
                <div className="ext-task-inline-subtask-row">
                    <i className="ti ti-circle ext-task-inline-circle" />
                    <input
                        type="text"
                        className="ext-task-inline-input"
                        placeholder="Title"
                        value={inlineSubtaskTitle}
                        onChange={(e) => setInlineSubtaskTitle(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && inlineSubtaskTitle.trim()) {
                                e.preventDefault();
                                handleInlineSubtaskSubmit();
                            }
                            if (e.key === "Escape") {
                                closeInlineSubtask();
                            }
                        }}
                        autoFocus
                    />
                </div>
                <div className="ext-task-inline-subtask-row">
                    <i className="ti ti-align-left ext-task-inline-icon" />
                    <input
                        type="text"
                        className="ext-task-inline-input details"
                        placeholder="Details"
                        value={inlineSubtaskNotes}
                        onChange={(e) => setInlineSubtaskNotes(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && inlineSubtaskTitle.trim()) {
                                e.preventDefault();
                                handleInlineSubtaskSubmit();
                            }
                            if (e.key === "Escape") {
                                closeInlineSubtask();
                            }
                        }}
                    />
                </div>
                <div className="ext-task-inline-date-row">
                    <button
                        className={classNames("ext-task-date-chip", { active: inlineSubtaskDueDate === dayjs().format("YYYY-MM-DD") })}
                        onClick={() => setInlineSubtaskDueDate(dayjs().format("YYYY-MM-DD"))}
                    >
                        Today
                    </button>
                    <button
                        className={classNames("ext-task-date-chip", { active: inlineSubtaskDueDate === dayjs().add(1, "day").format("YYYY-MM-DD") })}
                        onClick={() => setInlineSubtaskDueDate(dayjs().add(1, "day").format("YYYY-MM-DD"))}
                    >
                        Tomorrow
                    </button>
                    <button
                        className="ext-task-date-chip icon"
                        onClick={() => inlineSubtaskDateRef.current?.showPicker?.()}
                    >
                        <i className="ti ti-calendar" />
                    </button>
                    <input
                        ref={inlineSubtaskDateRef}
                        type="date"
                        className="ext-task-hidden-date"
                        value={inlineSubtaskDueDate}
                        onChange={(e) => setInlineSubtaskDueDate(e.target.value)}
                    />
                    {inlineSubtaskDueDate && (
                        <span className="ext-task-inline-selected-date">
                            {dayjs(inlineSubtaskDueDate).format("MMM D")}
                            <button className="ext-task-inline-clear-date" onClick={() => setInlineSubtaskDueDate("")}>
                                <i className="ti ti-x" />
                            </button>
                        </span>
                    )}
                </div>
                <div className="ext-task-inline-actions">
                    <button
                        className="ext-task-inline-save"
                        onClick={handleInlineSubtaskSubmit}
                        disabled={!inlineSubtaskTitle.trim()}
                    >
                        Save
                    </button>
                    <button className="ext-task-inline-cancel" onClick={closeInlineSubtask}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    const renderDeadlinePicker = (taskId: number) => {
        if (deadlineTaskId !== taskId) return null;

        return (
            <div className="ext-task-deadline-picker">
                <div className="ext-task-deadline-chips">
                    <button
                        className="ext-task-date-chip"
                        onClick={() => handleAddDeadline(taskId, dayjs().format("YYYY-MM-DD"))}
                    >
                        Today
                    </button>
                    <button
                        className="ext-task-date-chip"
                        onClick={() => handleAddDeadline(taskId, dayjs().add(1, "day").format("YYYY-MM-DD"))}
                    >
                        Tomorrow
                    </button>
                    <button
                        className="ext-task-date-chip icon"
                        onClick={() => deadlineDateInputRef.current?.showPicker?.()}
                    >
                        <i className="ti ti-calendar" />
                    </button>
                    <input
                        ref={deadlineDateInputRef}
                        type="date"
                        className="ext-task-hidden-date"
                        value={deadlineDate}
                        onChange={(e) => {
                            setDeadlineDate(e.target.value);
                            if (e.target.value) {
                                handleAddDeadline(taskId, e.target.value);
                            }
                        }}
                    />
                    <button
                        className="ext-task-date-chip cancel"
                        onClick={() => { setDeadlineTaskId(null); setDeadlineDate(""); }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    const renderTaskItem = (task: CalendarTask, isSubtask: boolean = false) => {
        const dueLabel = formatDueDate(task.due_datetime);
        const overdue = isDuePast(task.due_datetime) && !task.is_completed;
        const priorityBadge = getPriorityBadge(task.priority);
        const isStarred = starredTaskIds.has(task.calendar_task_id);
        const subtasks = getSubtasksForTask(task);
        const isRootTask = !task.parent_task_id;

        return (
            <div key={task.calendar_task_id}>
                <div className={classNames("ext-task-item", { "ext-task-subtask-item": isSubtask })}>
                    <div className="ext-task-item-main">
                        <div className="ext-task-item-left">
                            {subtasks.length > 0 && (
                                <button
                                    className={classNames("ext-task-expand-btn", { expanded: expandedTaskIds.has(task.calendar_task_id) })}
                                    onClick={() => toggleTaskExpanded(task.calendar_task_id)}
                                >
                                    <i className="ti ti-chevron-right" />
                                </button>
                            )}
                            {task.calendar_task_group_id && (
                                <button
                                    className="ext-task-check-btn"
                                    onClick={() => toggleTaskCompletion(task.calendar_task_id)}
                                    disabled={completingTaskIds.has(task.calendar_task_id)}
                                >
                                    {completingTaskIds.has(task.calendar_task_id) ? (
                                        <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '12px', height: '12px' }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    ) : (
                                        <i className="ti ti-circle" />
                                    )}
                                </button>
                            )}
                            <div className="ext-task-item-content">
                                <div className="ext-task-item-title" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    {task.task_source === "calendar" && (
                                        <img src="../assets/img/platform-icons/Tasks.png" alt="Google Tasks" title="Google Tasks" style={{ width: "16px", height: "16px", flexShrink: 0 }} />
                                    )}
                                    {task.task_source === "crm" && (
                                        <span title="CRM Task" style={{ fontSize: "0.6rem", fontWeight: "bold", background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", padding: "2px 5px", borderRadius: "4px", lineHeight: 1, flexShrink: 0 }}>CRM</span>
                                    )}
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{task.title}</span>
                                    {task.task_source === "crm" && task.related_task_id && (
                                        <i
                                            className="ti ti-external-link ms-1"
                                            style={{ fontSize: "0.8rem", color: "#824eeb", cursor: "pointer", flexShrink: 0 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenCRMTask(task.related_task_id as number);
                                            }}
                                        />
                                    )}
                                </div>
                                {(task.notes || dueLabel || priorityBadge || !task.calendar_task_group_id) && (
                                    <div className="ext-task-item-meta">
                                        {dueLabel && (
                                            <span className={classNames("ext-task-due", { overdue })}>
                                                <i className="ti ti-calendar-event" />
                                                {dueLabel}
                                            </span>
                                        )}
                                        {priorityBadge && (
                                            <span
                                                className="ext-task-priority"
                                                style={{
                                                    color: priorityBadge.color,
                                                    background: priorityBadge.bg,
                                                }}
                                            >
                                                {priorityBadge.label}
                                            </span>
                                        )}
                                        {task.notes && (
                                            <span className="ext-task-notes-hint">
                                                {task.notes.substring(0, 60)}
                                                {task.notes.length > 60 ? "..." : ""}
                                            </span>
                                        )}
                                        {!task.calendar_task_group_id && (
                                            <span className="badge bg-soft-warning text-warning ms-2" style={{ fontSize: '0.65rem', border: '1px solid rgba(245,158,11,0.2)' }}>
                                                <i className="ti ti-alert-circle me-1" />
                                                Not Synced
                                            </span>
                                        )}
                                    </div>
                                )}


                                {/* {isRootTask && subtasks.length > 0 && (
                                    <div className="ext-task-subtask-indicator">
                                        <i className="ti ti-subtask" />
                                        <span>
                                            {subtasks.filter((s) => s.is_completed).length}/{subtasks.length}
                                        </span>
                                    </div>
                                )} */}


                            </div>
                        </div>
                        <div className="ext-task-item-right">
                            <div
                                className={classNames("ext-task-item-actions", { "menu-open": taskMenuOpen === task.calendar_task_id })}
                                ref={taskMenuOpen === task.calendar_task_id ? taskMenuRef : undefined}
                            >
                                <button
                                    className="ext-task-icon-btn small"
                                    onClick={(e) => toggleTaskMenu(e, task.calendar_task_id)}
                                >
                                    <i className="ti ti-dots-vertical" />
                                </button>
                                {taskMenuOpen === task.calendar_task_id && (
                                    <div
                                        ref={taskDropdownRef}
                                        className="ext-task-dropdown-menu task-context"
                                        style={{ top: menuPos.top, left: menuPos.left }}
                                    >
                                        <button
                                            className="ext-task-dropdown-item"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setTaskMenuOpen(null);
                                                setDeadlineTaskId(task.calendar_task_id);
                                                setDeadlineDate("");
                                            }}
                                        >
                                            <i className="ti ti-calendar-due me-2" />
                                            Add deadline
                                        </button>
                                        {isRootTask && (
                                            <button
                                                className="ext-task-dropdown-item"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTaskMenuOpen(null);
                                                    setInlineSubtaskParentId(task.calendar_task_id);
                                                    setInlineSubtaskTitle("");
                                                    setInlineSubtaskNotes("");
                                                    setInlineSubtaskDueDate("");
                                                }}
                                            >
                                                <i className="ti ti-subtask me-2" />
                                                Add a subtask
                                            </button>
                                        )}
                                        {task.calendar_task_group_id && (
                                            <>
                                                <button
                                                    className="ext-task-dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(task);
                                                    }}
                                                >
                                                    <i className="ti ti-edit me-2" />
                                                    Edit
                                                </button>
                                                <button
                                                    className="ext-task-dropdown-item text-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteConfirmTaskId(task.calendar_task_id);
                                                        setTaskMenuOpen(null);
                                                    }}
                                                >
                                                    <i className="ti ti-trash me-2" />
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                className={classNames("ext-task-star-btn", { starred: isStarred })}
                                onClick={() => toggleStar(task.calendar_task_id)}
                            >
                                <i className={classNames("ti", isStarred ? "ti-star-filled" : "ti-star")} />
                            </button>
                        </div>
                    </div>
                </div>
                {renderDeadlinePicker(task.calendar_task_id)}
                {isRootTask && subtasks.length > 0 && expandedTaskIds.has(task.calendar_task_id) && (
                    <div className="ext-task-subtask-list">
                        {subtasks.filter((s) => !s.is_completed).map((sub) => renderTaskItem(sub, true))}
                        {subtasks.filter((s) => s.is_completed).map((sub) => renderCompletedTaskItem(sub, true))}
                    </div>
                )}
                {isRootTask && renderInlineSubtaskForm(task.calendar_task_id)}
            </div>
        );
    };

    const renderCompletedTaskItem = (task: CalendarTask, isSubtask: boolean = false) => {
        const isStarred = starredTaskIds.has(task.calendar_task_id);
        const subtasks = getSubtasksForTask(task);
        const isRootTask = !task.parent_task_id;

        return (
            <div key={task.calendar_task_id}>
                <div className={classNames("ext-task-item completed", { "ext-task-subtask-item": isSubtask })}>
                    <div className="ext-task-item-main">
                        <div className="ext-task-item-left">
                            {subtasks.length > 0 && (
                                <button
                                    className={classNames("ext-task-expand-btn", { expanded: expandedTaskIds.has(task.calendar_task_id) })}
                                    onClick={() => toggleTaskExpanded(task.calendar_task_id)}
                                >
                                    <i className="ti ti-chevron-right" />
                                </button>
                            )}
                            {task.calendar_task_group_id && (
                                <button
                                    className="ext-task-check-btn checked"
                                    onClick={() => toggleTaskCompletion(task.calendar_task_id)}
                                    disabled={completingTaskIds.has(task.calendar_task_id)}
                                >
                                    {completingTaskIds.has(task.calendar_task_id) ? (
                                        <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '12px', height: '12px' }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    ) : (
                                        <i className="ti ti-circle-check-filled" />
                                    )}
                                </button>
                            )}
                            <div className="ext-task-item-content">
                                <div className="ext-task-item-title completed">
                                    {task.title}
                                </div>
                                {(task.completed_datetime || !task.calendar_task_group_id) && (
                                    <div className="ext-task-item-meta">
                                        {task.completed_datetime && (
                                            <span className="ext-task-completed-date">
                                                Completed {dayjs(task.completed_datetime).format("MMM D")}
                                            </span>
                                        )}
                                        {!task.calendar_task_group_id && (
                                            <span className="badge bg-soft-warning text-warning ms-2" style={{ fontSize: '0.65rem', border: '1px solid rgba(245,158,11,0.2)' }}>
                                                <i className="ti ti-alert-circle me-1" />
                                                Not Synced
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="ext-task-item-right">
                            <div
                                className={classNames("ext-task-item-actions", { "menu-open": taskMenuOpen === task.calendar_task_id })}
                                ref={taskMenuOpen === task.calendar_task_id ? taskMenuRef : undefined}
                            >
                                <button
                                    className="ext-task-icon-btn small"
                                    onClick={(e) => toggleTaskMenu(e, task.calendar_task_id)}
                                >
                                    <i className="ti ti-dots-vertical" />
                                </button>
                                {taskMenuOpen === task.calendar_task_id && (
                                    <div
                                        ref={taskDropdownRef}
                                        className="ext-task-dropdown-menu task-context"
                                        style={{ top: menuPos.top, left: menuPos.left }}
                                    >
                                        {task.calendar_task_group_id && (
                                            <>
                                                <button
                                                    className="ext-task-dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(task);
                                                    }}
                                                >
                                                    <i className="ti ti-edit me-2" />
                                                    Edit
                                                </button>
                                                <button
                                                    className="ext-task-dropdown-item text-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteConfirmTaskId(task.calendar_task_id);
                                                        setTaskMenuOpen(null);
                                                    }}
                                                >
                                                    <i className="ti ti-trash me-2" />
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                className={classNames("ext-task-star-btn", { starred: isStarred })}
                                onClick={() => toggleStar(task.calendar_task_id)}
                            >
                                <i className={classNames("ti", isStarred ? "ti-star-filled" : "ti-star")} />
                            </button>
                        </div>
                    </div>
                </div>
                {isRootTask && subtasks.length > 0 && expandedTaskIds.has(task.calendar_task_id) && (
                    <div className="ext-task-subtask-list">
                        {subtasks.filter((s) => !s.is_completed).map((sub) => renderTaskItem(sub, true))}
                        {subtasks.filter((s) => s.is_completed).map((sub) => renderCompletedTaskItem(sub, true))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="page-header">
                    <div className="row align-items-center">
                        <div className="col-sm-12 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-4">
                                <h4 className="page-title">Tasks</h4>
                            </div>
                            <div className="calendar-tasks-toggle mb-2">
                                <button className="toggle-btn" onClick={() => navigate("/application/calendar")}>
                                    <i className="ti ti-calendar" />
                                    Calendar
                                </button>
                                <button className="toggle-btn active">
                                    <i className="ti ti-checklist" />
                                    Tasks
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className={classNames("ext-task-sidebar-col", { collapsed: sidebarCollapsed })}>
                        <div className="ext-task-sidebar">
                            <div className="ext-task-sidebar-header" />

                            {!sidebarCollapsed && (
                                <>
                                    <div className="ext-task-sidebar-filters">
                                        <button
                                            className={classNames("ext-task-filter-btn", { active: sidebarFilter === "all" })}
                                            onClick={() => setSidebarFilter("all")}
                                        >
                                            <i className="ti ti-checklist" />
                                            <span>All tasks</span>
                                        </button>
                                    </div>

                                    <div className="ext-task-sidebar-divider" />

                                    <div className="ext-task-sidebar-section">
                                        <div className="ext-task-sidebar-section-header">
                                            <span>Lists</span>
                                        </div>
                                        {[...allTaskGroups, ...(tasksByGroup["unsynced"]?.length ? [{ calendar_task_group_id: "unsynced" as any, task_group_name: "Not Synced" }] : [])].map((group) => {
                                            const count = (tasksByGroup[group.calendar_task_group_id] || []).filter(
                                                (t) => !t.is_completed
                                            ).length;
                                            const isSelected = selectedGroupIds.has(group.calendar_task_group_id);
                                            return (
                                                <div
                                                    key={group.calendar_task_group_id}
                                                    className={classNames("ext-task-list-item", { active: isSelected })}
                                                    onClick={() => toggleGroupSelection(group.calendar_task_group_id)}
                                                >
                                                    <div className="ext-task-list-item-left">
                                                        <div className="ext-task-checkbox-wrapper">
                                                            <input
                                                                type="checkbox"
                                                                className="ext-task-checkbox"
                                                                checked={isSelected}
                                                                onChange={() => { }}
                                                            />
                                                            <span className="ext-task-checkbox-custom">
                                                                {isSelected && <i className="ti ti-check" />}
                                                            </span>
                                                        </div>
                                                        <span className="ext-task-list-name">{group.task_group_name}</span>
                                                    </div>
                                                    {count > 0 && (
                                                        <span className="ext-task-list-count">{count}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {groupsLoading && (
                                            <div className="ext-task-sidebar-loading">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="ext-task-main-col">
                        {loading || crmTasksLoading ? (
                            <div className="ext-task-loading">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : calendarAuthsLoaded && allTaskGroups.length === 0 ? (
                            crmTasks.length === 0 ? (
                                <div className="ext-task-empty">
                                    <div className="ext-task-empty-icon">
                                        <i className="ti ti-checklist" />
                                    </div>
                                    <h5>No tasks found</h5>
                                    <p>No tasks available for this account.</p>
                                </div>
                            ) : (
                                <div className="ext-task-columns">
                                    <div className="ext-task-column">
                                        <div className="ext-task-column-header">
                                            <h6 className="ext-task-column-title">Tasks</h6>
                                            {(totalTasksCount > 0 || totalCrmTasksCount > 0) && (
                                                <span className="badge bg-primary text-white" style={{ fontSize: '0.8rem', padding: '0.4em 0.8em', borderRadius: "30px" }}>
                                                    Total: {calendarAuthsLoaded && allTaskGroups.length === 0 ? totalCrmTasksCount : totalTasksCount}
                                                </span>
                                            )}
                                        </div>
                                        <div className="ext-task-list">
                                            {crmTasks.filter((t) => !t.is_deleted).map((task) => {
                                                const dueLabel = formatDueDate(task.due_date);
                                                const overdue = isDuePast(task.due_date);
                                                const priorityBadge = getPriorityBadge(task.priority);
                                                return (
                                                    <div key={task.task_id} className="ext-task-item">
                                                        <div className="ext-task-item-main">
                                                            <div className="ext-task-item-left">
                                                                <div className="ext-task-item-content">
                                                                    <div className="ext-task-item-title" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{task.title}</span>
                                                                        <i
                                                                            className="ti ti-external-link ms-1"
                                                                            style={{ fontSize: "0.8rem", color: "#824eeb", cursor: "pointer", flexShrink: 0 }}
                                                                            onClick={() => handleOpenCRMTask(task.task_id)}
                                                                        />
                                                                    </div>
                                                                    {(dueLabel || priorityBadge || task.description) && (
                                                                        <div className="ext-task-item-meta">
                                                                            {dueLabel && (
                                                                                <span className={classNames("ext-task-due", { overdue })}>
                                                                                    <i className="ti ti-calendar-event" />
                                                                                    {dueLabel}
                                                                                </span>
                                                                            )}
                                                                            {priorityBadge && (
                                                                                <span
                                                                                    className="ext-task-priority"
                                                                                    style={{ color: priorityBadge.color, background: priorityBadge.bg }}
                                                                                >
                                                                                    {priorityBadge.label}
                                                                                </span>
                                                                            )}
                                                                            {task.description && (
                                                                                <span className="ext-task-notes-hint">
                                                                                    {task.description.substring(0, 60)}{task.description.length > 60 ? "..." : ""}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )
                        ) : filteredGroups.length === 0 ? (
                            <div className="ext-task-empty">
                                <div className="ext-task-empty-icon">
                                    <i className="ti ti-checklist" />
                                </div>
                                <h5>No task lists found</h5>
                                <p>Tasks from your linked calendars will appear here.</p>
                            </div>
                        ) : (
                            <div className="ext-task-columns">
                                {filteredGroups.map((group) => {
                                    const groupId = group.calendar_task_group_id;
                                    const allGroupTasks = getFilteredTasksForGroup(groupId);
                                    const rootPendingTasks = applySorting(
                                        allGroupTasks.filter((t) => !t.is_completed && !t.parent_task_id),
                                        groupId
                                    );
                                    const rootCompletedTasks = applySorting(
                                        allGroupTasks.filter((t) => t.is_completed && !t.parent_task_id),
                                        groupId
                                    );
                                    const showCompleted = completedVisible[groupId] || false;

                                    return (
                                        <div key={groupId} className="ext-task-column">
                                            <div className="ext-task-column-header">
                                                <h6 className="ext-task-column-title">
                                                    {group.task_group_name}
                                                </h6>
                                            </div>

                                            {(groupId as any) !== "unsynced" && (
                                                <button
                                                    className="ext-task-add-btn"
                                                    onClick={() => openCreateModal(groupId as number)}
                                                >
                                                    <i className="ti ti-circle-plus" />
                                                    <span>Add a task</span>
                                                </button>
                                            )}

                                            <div className="ext-task-list">
                                                {rootPendingTasks.map((task) => renderTaskItem(task))}
                                            </div>

                                            {rootCompletedTasks.length > 0 && (
                                                <div className="ext-task-completed-section">
                                                    <button
                                                        className="ext-task-completed-toggle"
                                                        onClick={() =>
                                                            setCompletedVisible((prev) => ({
                                                                ...prev,
                                                                [groupId]: !prev[groupId],
                                                            }))
                                                        }
                                                    >
                                                        <i className={classNames("ti", showCompleted ? "ti-chevron-down" : "ti-chevron-right")} />
                                                        <span>Completed ({rootCompletedTasks.length})</span>
                                                    </button>
                                                    {showCompleted && (
                                                        <div className="ext-task-list completed">
                                                            {rootCompletedTasks.map((task) => renderCompletedTaskItem(task))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div ref={sentinelRef} style={{ height: '1px' }} />
                        {(loadingMore || crmTasksLoadingMore) && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {showCreateModal && (
                    <>
                        <div className="ext-task-modal-backdrop" onClick={closeCreateModal} />
                        <div className="ext-task-modal">
                            <div className="ext-task-modal-header">
                                <span className="ext-task-modal-header-title">New Task</span>
                                <button className="ext-task-modal-close" onClick={closeCreateModal}>
                                    <i className="ti ti-x" />
                                </button>
                            </div>
                            <div className="ext-task-modal-body">
                                <input
                                    type="text"
                                    className="ext-task-modal-title-input"
                                    placeholder="Add title"
                                    value={createForm.title}
                                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                                    autoFocus
                                />
                                <div className="ext-task-modal-field">
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="hasDueDateCheck"
                                            checked={createForm.hasDueDate}
                                            onChange={(e) => setCreateForm({ ...createForm, hasDueDate: e.target.checked })}
                                        />
                                        <label htmlFor="hasDueDateCheck" className="ext-task-modal-label">Add due date</label>
                                    </div>
                                </div>
                                {createForm.hasDueDate && (
                                    <div className="ext-task-modal-field">
                                        <i className="ti ti-clock" />
                                        <div className="ext-task-modal-datetime">
                                            <input
                                                type="date"
                                                className="ext-task-modal-date"
                                                value={createForm.dueDate}
                                                onChange={(e) => setCreateForm({ ...createForm, dueDate: e.target.value })}
                                            />
                                            <input
                                                type="time"
                                                className="ext-task-modal-time"
                                                value={createForm.dueTime}
                                                onChange={(e) => setCreateForm({ ...createForm, dueTime: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="ext-task-modal-field description">
                                    <i className="ti ti-align-left" />
                                    <textarea
                                        className="ext-task-modal-textarea"
                                        placeholder="Add a description"
                                        rows={3}
                                        value={createForm.description}
                                        onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                                    />
                                </div>
                                <div className="ext-task-modal-field">
                                    <i className="ti ti-list" />
                                    <select
                                        className="ext-task-modal-select list-select"
                                        value={createForm.listId || ""}
                                        onChange={(e) =>
                                            setCreateForm({ ...createForm, listId: e.target.value ? Number(e.target.value) : null })
                                        }
                                    >
                                        {(allTaskGroups.length > 0 ? allTaskGroups : derivedGroups).map((g) => (
                                            <option key={g.calendar_task_group_id} value={g.calendar_task_group_id}>
                                                {g.task_group_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="ext-task-modal-footer">
                                <button className="ext-task-save-btn" onClick={handleSaveTask} disabled={savingTask}>
                                    {savingTask ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {showEditModal && editingTask && (
                    <>
                        <div className="ext-task-modal-backdrop" onClick={closeEditModal} />
                        <div className="ext-task-modal">
                            <div className="ext-task-modal-header">
                                <span className="ext-task-modal-header-title">Edit Task</span>
                                <button className="ext-task-modal-close" onClick={closeEditModal}>
                                    <i className="ti ti-x" />
                                </button>
                            </div>
                            <div className="ext-task-modal-body">
                                <input
                                    type="text"
                                    className="ext-task-modal-title-input"
                                    placeholder="Task title"
                                    value={editForm.title}
                                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                    autoFocus
                                />
                                <div className="ext-task-modal-field">
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="editHasDueDateCheck"
                                            checked={editForm.hasDueDate}
                                            onChange={(e) => setEditForm({ ...editForm, hasDueDate: e.target.checked })}
                                        />
                                        <label htmlFor="editHasDueDateCheck" className="ext-task-modal-label">Due date</label>
                                    </div>
                                </div>
                                {editForm.hasDueDate && (
                                    <div className="ext-task-modal-field">
                                        <i className="ti ti-clock" />
                                        <div className="ext-task-modal-datetime">
                                            <input
                                                type="date"
                                                className="ext-task-modal-date"
                                                value={editForm.dueDate}
                                                onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                                            />
                                            <input
                                                type="time"
                                                className="ext-task-modal-time"
                                                value={editForm.dueTime}
                                                onChange={(e) => setEditForm({ ...editForm, dueTime: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="ext-task-modal-field description">
                                    <i className="ti ti-align-left" />
                                    <textarea
                                        className="ext-task-modal-textarea"
                                        placeholder="Add notes"
                                        rows={3}
                                        value={editForm.notes}
                                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="ext-task-modal-footer">
                                <button className="ext-task-save-btn" onClick={handleUpdateTask} disabled={savingEdit}>
                                    {savingEdit ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {deleteConfirmTaskId !== null && (
                    <>
                        <div className="ext-task-modal-backdrop" onClick={() => setDeleteConfirmTaskId(null)} />
                        <div className="ext-task-confirm-modal">
                            <div className="ext-task-confirm-icon">
                                <i className="ti ti-alert-triangle" />
                            </div>
                            <h6>Delete Task</h6>
                            <p>Are you sure you want to delete this task? This action cannot be undone.</p>
                            <div className="ext-task-confirm-actions">
                                <button className="ext-task-confirm-cancel" onClick={() => setDeleteConfirmTaskId(null)}>
                                    Cancel
                                </button>
                                <button className="ext-task-confirm-delete" onClick={() => handleDeleteTask(deleteConfirmTaskId)}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {taskError && (
                <>
                    <div className="ext-task-modal-backdrop ext-task-error-backdrop" onClick={() => setTaskError(null)} />
                    <div className="ext-task-confirm-modal ext-task-error-modal" role="alertdialog" aria-labelledby="ext-task-error-title">
                        <div className="ext-task-confirm-icon">
                            <i className="ti ti-alert-circle" />
                        </div>
                        <h6 id="ext-task-error-title">{taskError.title}</h6>
                        <p className="ext-task-error-message">{taskError.message}</p>
                        <div className="ext-task-confirm-actions">
                            <button className="ext-task-error-ok" onClick={() => setTaskError(null)} autoFocus>
                                Got it
                            </button>
                        </div>
                    </div>
                </>
            )}
            <TaskDetailModal
                isVisible={isTaskModalVisible}
                taskDetails={taskDetails}
                isLoading={isLoadingTaskDetails}
                onClose={() => setIsTaskModalVisible(false)}
                onTaskUpdate={refreshFirstPage}
                tenantId={selectedTenantId}
            />
        </div>
    );
};

export default ExternalTask;